import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import socialAuthService from 'api/services/socialAuth.service';

/**
 * Where the browser lands after signing in with a provider.
 *
 * <p>The URL carries a single-use code, never the session. The code is exchanged over
 * POST for the token and the user, which is why a JWT never appears in browser
 * history, in a Referer header, or in the CDN's access logs.
 *
 * <p>The code is good once and for sixty seconds, so this must not run twice —
 * React's development StrictMode deliberately double-invokes effects, and a second
 * call would consume an already-spent code and report a failed sign-in after a
 * successful one. The ref guards that.
 */
const SocialComplete: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { establishSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const code = params.get('code');
    if (!code) {
      setError('That sign-in link is incomplete. Please try again.');
      return;
    }

    socialAuthService
      .complete(code)
      .then((session) => {
        if (session?.user) {
          establishSession(session.user);
          navigate('/', { replace: true });
        } else {
          setError('That sign-in has expired. Please try again.');
        }
      })
      .catch(() => setError('We could not complete that sign-in. Please try again.'));
  }, [params, navigate, establishSession]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm text-center">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-slate-900">Sign-in failed</h1>
            <p className="mt-2 text-sm text-slate-600">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="mt-5 inline-flex items-center justify-center rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <div
              className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-700"
              role="status"
              aria-label="Signing you in"
            />
            <p className="mt-4 text-sm text-slate-600">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
};

export default SocialComplete;
